import { Typography } from "antd";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import moment from "moment";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import RepositoryAPI from "restAPI/RepositoryAPI";
import { FieldTypes, checkNumberLimit } from "utils/formUtils";

const { Text } = Typography;

// Type definitions for component props
interface Repository {
  id: number | string;
  name?: string;
}

interface CodeInsightsModalProps {
  repository: Repository;
  installation?: any;
  [key: string]: any;
}

// Type definition for form values
interface FormValues {
  branch: string;
  lookbackPeriod: number;
}

const CodeInsightsModal: FC<CodeInsightsModalProps> = ({
  repository,
  installation,
  ...props
}) => {
  // Fetch MCC branches for the repository
  const [maxLookBack, setMaxLookBack] = useState<number>(30);
  const [lookbackPeriod, setLookbackPeriod] = useState<number>(0);

  const getCalculatedDate = useCallback((days: number) => {
    return moment().subtract(days, "days").format("MMMM D, YYYY");
  }, []);

  useEffect(() => {
    const { promise, cancel } = GlobalSettingAPI.getCodeInsightsBeConfig();
    promise
      .then((res: any) => {
        const days = Number(res?.maxLookBackDuration);
        setMaxLookBack(days > 0 ? days : 30);
      })
      .catch(() => setMaxLookBack(30));
    return cancel;
  }, []);

  const getMCCBranches = useCallback(
    (params: any) => {
      const { promise, cancel } = RepositoryAPI.getMCCBranches(
        repository.id,
        params,
        true,
      );
      const newPromise = promise.then((response) => {
        return response.data;
      });
      return { promise: newPromise, cancel };
    },
    [repository.id],
  );

  const onSave = useCallback(
    async (fields: Record<string, any>): Promise<any> => {
      const payload = {
        branches: [fields.branch],
        durationInDays: fields.lookbackPeriod.toString(),
      };

      // Trigger the MCC scan
      const { promise } = RepositoryAPI.triggerMCCSync(repository.id, payload);
      await promise;

      message.success("Code analysis scan triggered successfully");
    },
    [repository],
  );

  const modalProps = {
    okText: "Apply",
  };

  const fields = useMemo(
    () => [
      {
        label: "Branch",
        name: "branch",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        required: true,
        notFoundContent: "No branches defined for this repository",
        props: {
          api: getMCCBranches,
          showDefaultError: false,
          placeholder: "Select branch",
          searchParamName: "query",
        },
        rules: [
          {
            required: true,
            message: "Please select a branch",
          },
        ],
      },
      {
        label: "Look-back Period(days)",
        name: "lookbackPeriod",
        type: FieldTypes.NUMBER,
        props: {
          placeholder: "Enter number of days",
          min: 1,
          max: maxLookBack,
          style: { width: 180 },
        },
        rules: [
          {
            required: true,
            message: "Please enter the look-back period",
          },
          checkNumberLimit({ min: 1, max: maxLookBack }),
        ],
      },
      {
        render: () => {
          return lookbackPeriod >= 1 ? (
            <div style={{ marginBottom: 16 }}>
              <Text style={{ color: "#666666" }}>
                We will inspect historical pull requests back to{" "}
                {getCalculatedDate(lookbackPeriod)}
              </Text>
            </div>
          ) : null;
        },
      },
    ],
    [getMCCBranches, maxLookBack, lookbackPeriod, getCalculatedDate],
  );

  return (
    <BaseModalForm
      {...props}
      modalProps={modalProps}
      fields={[
        {
          render: () => (
            <div style={{ marginBottom: 16 }}>
              <Text style={{ color: "#666666" }}>
                Configure branch and historical look-back for material code
                change detection
              </Text>
            </div>
          ),
        },
        ...fields,
      ]}
      onOk={onSave}
      onValuesChange={(changedValues: any) => {
        if (changedValues.lookbackPeriod && changedValues.lookbackPeriod > 0) {
          setLookbackPeriod(changedValues.lookbackPeriod);
        }
      }}
      title={`Code Insights - ${repository?.name}`}
    />
  );
};

export default CodeInsightsModal;
